import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

const TERMS_VERSION = '1.0';
const STORAGE_KEY = 'terms_acceptance';
const CONSENT_EVENT = 'partxplorer:consent-changed';

interface TermsAcceptance {
  accepted: boolean;
  timestamp: number;
  version: string;
  consentId?: string;
}

interface ConsentContextType {
  hasAccepted: boolean;
  isLoading: boolean;
  acceptTerms: () => Promise<void>;
  revokeConsent: () => Promise<void>;
  resetAcceptance: () => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return context;
};

interface ConsentProviderProps {
  children: ReactNode;
}

export const ConsentProvider = ({ children }: ConsentProviderProps) => {
  const [hasAccepted, setHasAccepted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadAcceptanceState = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data: TermsAcceptance = JSON.parse(stored);
        if (data.accepted && data.version === TERMS_VERSION) {
          setHasAccepted(true);
        } else {
          setHasAccepted(false);
        }
      } catch (error) {
        console.error('Error parsing terms acceptance:', error);
        setHasAccepted(false);
      }
    } else {
      setHasAccepted(false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadAcceptanceState();

    // Listen for consent change events from other tabs/components
    const handleConsentChange = () => {
      console.log('[ConsentContext] Consent change event received');
      loadAcceptanceState();
    };

    window.addEventListener(CONSENT_EVENT, handleConsentChange);
    window.addEventListener('storage', handleConsentChange);

    return () => {
      window.removeEventListener(CONSENT_EVENT, handleConsentChange);
      window.removeEventListener('storage', handleConsentChange);
    };
  }, []);

  const acceptTerms = async () => {
    try {
      // Record consent in database via edge function
      const { data, error } = await supabase.functions.invoke('record-consent', {
        body: {
          termsVersion: TERMS_VERSION,
          consentType: 'terms_and_privacy'
        }
      });

      if (error) {
        console.error('Error recording consent:', error);
      }

      const acceptanceData: TermsAcceptance = {
        accepted: true,
        timestamp: Date.now(),
        version: TERMS_VERSION,
        consentId: data?.consentId
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(acceptanceData));
      setHasAccepted(true);
      
      // Dispatch custom event to notify all components
      window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { accepted: true } }));
    } catch (error) {
      console.error('Error accepting terms:', error);
      const acceptanceData: TermsAcceptance = {
        accepted: true,
        timestamp: Date.now(),
        version: TERMS_VERSION
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(acceptanceData));
      setHasAccepted(true);
      window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { accepted: true } }));
    }
  };

  const revokeConsent = async () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data: TermsAcceptance = JSON.parse(stored);
        
        if (data.consentId) {
          const { error } = await supabase
            .from('user_consent')
            .update({ revoked_at: new Date().toISOString() })
            .eq('id', data.consentId);

          if (error) {
            console.error('Error revoking consent in database:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error revoking consent:', error);
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      setHasAccepted(false);
      
      // Dispatch custom event to notify all components
      window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { accepted: false } }));
    }
  };

  const resetAcceptance = () => {
    localStorage.removeItem(STORAGE_KEY);
    setHasAccepted(false);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { accepted: false } }));
  };

  return (
    <ConsentContext.Provider
      value={{
        hasAccepted,
        isLoading,
        acceptTerms,
        revokeConsent,
        resetAcceptance
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};
